import React, { useMemo, useState, useCallback } from 'react';

function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const cp1x = pts[i - 1].x + (pts[i].x - pts[i - 1].x) * 0.45;
    const cp1y = pts[i - 1].y;
    const cp2x = pts[i].x - (pts[i].x - pts[i - 1].x) * 0.45;
    const cp2y = pts[i].y;
    d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${pts[i].x} ${pts[i].y}`;
  }
  return d;
}

function areaPath(linePath, pts, bottomY) {
  if (!pts.length) return '';
  return `${linePath} L ${pts[pts.length - 1].x} ${bottomY} L ${pts[0].x} ${bottomY} Z`;
}

/**
 * Reference-style dual-line area chart with legend and hover tooltip.
 */
export default function DualLineActivityChart({
  series1 = [],
  series2 = [],
  labels = [],
  color1 = 'var(--text-secondary)',
  color2 = 'var(--text-muted)',
  label1 = 'Series 1',
  label2 = 'Series 2',
  height = 200,
  className = '',
}) {
  const [hover, setHover] = useState(null);

  const chart = useMemo(() => {
    const a = series1.map(Number).filter((v) => Number.isFinite(v));
    const b = series2.map(Number).filter((v) => Number.isFinite(v));
    const n = Math.max(a.length, b.length);
    if (n < 2) return null;

    const pad = { top: 16, bottom: 28, left: 8, right: 8 };
    const w = 480;
    const h = height;
    const innerW = w - pad.left - pad.right;
    const innerH = h - pad.top - pad.bottom;

    const all = [...a, ...b];
    const minV = Math.min(...all);
    const maxV = Math.max(...all);
    const range = maxV - minV || 1;

    const toX = (i, len) => pad.left + (i / Math.max(1, len - 1)) * innerW;
    const toY = (v) => pad.top + innerH - ((v - minV) / range) * innerH;

    const pts1 = a.map((v, i) => ({ x: toX(i, a.length), y: toY(v), v, i }));
    const pts2 = b.map((v, i) => ({ x: toX(i, b.length), y: toY(v), v, i }));

    const line1 = smoothPath(pts1);
    const line2 = smoothPath(pts2);
    const bottomY = h - pad.bottom;

    const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
      y: pad.top + innerH * (1 - t),
      value: Math.round(minV + range * t),
    }));

    return {
      w,
      h,
      pad,
      pts1,
      pts2,
      line1,
      line2,
      area1: areaPath(line1, pts1, bottomY),
      area2: areaPath(line2, pts2, bottomY),
      gridLines,
      n: Math.max(a.length, b.length),
    };
  }, [series1, series2, height]);

  const onMove = useCallback(
    (e) => {
      if (!chart) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * chart.w;
      const innerW = chart.w - chart.pad.left - chart.pad.right;
      const ratio = Math.max(0, Math.min(1, (x - chart.pad.left) / innerW));
      const idx = Math.round(ratio * (chart.n - 1));
      const p1 = chart.pts1[idx];
      const p2 = chart.pts2[idx];
      setHover({
        idx,
        x: chart.pad.left + ratio * innerW,
        p1,
        p2,
        label: labels[idx] || '',
      });
    },
    [chart, labels]
  );

  if (!chart) {
    return (
      <div
        className={`app-chart-empty ${className}`}
        style={{ height }}
      >
        Not enough data yet
      </div>
    );
  }

  const grad1 = 'dual-chart-g1';
  const grad2 = 'dual-chart-g2';

  return (
    <div className={`app-dual-chart ${className}`.trim()}>
      <div className="app-dual-chart__legend">
        <span className="app-dual-chart__legend-item">
          <span className="app-dual-chart__dot" style={{ background: color1 }} />
          {label1}
        </span>
        <span className="app-dual-chart__legend-item">
          <span className="app-dual-chart__dot" style={{ background: color2 }} />
          {label2}
        </span>
      </div>

      <div
        className="app-dual-chart__svg-wrap"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={`${label1} and ${label2} trend chart`}
      >
        <svg viewBox={`0 0 ${chart.w} ${chart.h}`} width="100%" height={chart.h} preserveAspectRatio="none">
          <defs>
            <linearGradient id={grad1} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color1} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color1} stopOpacity="0" />
            </linearGradient>
            <linearGradient id={grad2} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color2} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color2} stopOpacity="0" />
            </linearGradient>
          </defs>

          {chart.gridLines.map((g, i) => (
            <g key={i}>
              <line
                x1={chart.pad.left}
                x2={chart.w - chart.pad.right}
                y1={g.y}
                y2={g.y}
                stroke="var(--border-subtle)"
                strokeWidth={1}
              />
              <text
                x={chart.pad.left - 2}
                y={g.y + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--text-subtle)"
              >
                {g.value}
              </text>
            </g>
          ))}

          <path d={chart.area2} fill={`url(#${grad2})`} />
          <path d={chart.area1} fill={`url(#${grad1})`} />
          <path d={chart.line2} fill="none" stroke={color2} strokeWidth={2.5} strokeLinecap="round" />
          <path d={chart.line1} fill="none" stroke={color1} strokeWidth={2.5} strokeLinecap="round" />

          {labels.map((lbl, i) => {
            if (i % Math.ceil(labels.length / 8) !== 0 && i !== labels.length - 1) return null;
            const x = chart.pad.left + (i / Math.max(1, labels.length - 1)) * (chart.w - chart.pad.left - chart.pad.right);
            return (
              <text key={i} x={x} y={chart.h - 6} textAnchor="middle" fontSize={9} fill="var(--text-subtle)">
                {lbl}
              </text>
            );
          })}

          {hover && (
            <>
              <line
                x1={hover.x}
                x2={hover.x}
                y1={chart.pad.top}
                y2={chart.h - chart.pad.bottom}
                stroke="var(--text-subtle)"
                strokeWidth={1}
                strokeDasharray="4 4"
                opacity={0.5}
              />
              {hover.p1 && <circle cx={hover.p1.x} cy={hover.p1.y} r={4} fill={color1} stroke="#fff" strokeWidth={2} />}
              {hover.p2 && <circle cx={hover.p2.x} cy={hover.p2.y} r={4} fill={color2} stroke="#fff" strokeWidth={2} />}
            </>
          )}
        </svg>

        {hover && (hover.p1 || hover.p2) && (
          <div
            className="app-dual-chart__tooltip"
            style={{
              left: `${Math.max(8, Math.min(92, (hover.x / chart.w) * 100))}%`,
              top: 8,
            }}
          >
            {hover.label && <div className="app-dual-chart__tooltip-date">{hover.label}</div>}
            {hover.p1 && (
              <div style={{ color: color1 }}>
                {label1}: <strong>{Math.round(hover.p1.v)}</strong>
              </div>
            )}
            {hover.p2 && (
              <div style={{ color: color2 }}>
                {label2}: <strong>{Math.round(hover.p2.v)}</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
